import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';
import { useEffect, useRef } from 'react';
import './Aurora.css';

// GLSL ES 1.0 so the shader compiles under both WebGL1 and WebGL2 contexts
// (older iOS devices and Low Power Mode can hand out WebGL1 only).
const VERT = `
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec2 uResolution;
uniform float uBlend;

vec3 permute(vec3 x) {
  return mod(((x * 34.0) + 1.0) * x, 289.0);
}

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;

  vec3 color = uv.x < 0.5
    ? mix(uColor1, uColor2, uv.x * 2.0)
    : mix(uColor2, uColor3, (uv.x - 0.5) * 2.0);

  float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  height = exp(height);
  height = (uv.y * 2.0 - height + 0.2);
  float intensity = 0.6 * height;

  float midPoint = 0.20;
  float auroraAlpha = smoothstep(midPoint - uBlend * 0.5, midPoint + uBlend * 0.5, intensity);

  // Clamp so channels never exceed alpha: unclamped values produced invalid premultiplied
  // colour that composited as yellow/magenta blow-outs instead of the brand reds.
  vec3 auroraColor = clamp(intensity, 0.0, 1.0) * color;

  gl_FragColor = vec4(auroraColor * auroraAlpha, auroraAlpha);
}`;

const toRgb = (hex) => {
  const c = new Color(hex);
  return [c.r, c.g, c.b];
};

export default function Aurora({
  colorStops = ['#00d8ff', '#7cff67', '#00d8ff'],
  amplitude = 1.0,
  blend = 0.5,
  speed = 1.0,
}) {
  const propsRef = useRef({ amplitude, blend, speed });
  propsRef.current = { amplitude, blend, speed };
  const ctnDom = useRef(null);
  const colorKey = colorStops.join(',');

  useEffect(() => {
    const ctn = ctnDom.current;
    if (!ctn) return undefined;

    let renderer;
    try {
      // A soft gradient doesn't need retina resolution; capping DPR saves a lot of GPU/battery on iPhones.
      renderer = new Renderer({
        alpha: true,
        premultipliedAlpha: true,
        antialias: false,
        dpr: Math.min(window.devicePixelRatio || 1, 1.25),
      });
    } catch (error) {
      return undefined; // No WebGL: the CSS gradient fallback on the container stays visible.
    }

    const gl = renderer.gl;
    if (!gl) return undefined;

    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const geometry = new Triangle(gl);
    const [c1, c2, c3] = colorKey.split(',').map(toRgb);
    const program = new Program(gl, {
      vertex: VERT,
      fragment: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uAmplitude: { value: propsRef.current.amplitude },
        uColor1: { value: c1 },
        uColor2: { value: c2 },
        uColor3: { value: c3 },
        uResolution: { value: [1, 1] },
        uBlend: { value: propsRef.current.blend },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });

    ctn.appendChild(gl.canvas);

    const resize = () => {
      const width = Math.max(ctn.offsetWidth, 1);
      const height = Math.max(ctn.offsetHeight, 1);
      renderer.setSize(width, height);
      program.uniforms.uResolution.value = [gl.drawingBufferWidth, gl.drawingBufferHeight];
    };
    resize();

    // iOS fires resize constantly while the toolbar collapses; ResizeObserver only fires on real changes.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(ctn);
    else window.addEventListener('resize', resize);

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    const start = performance.now();

    const render = (now) => {
      const { speed: s, amplitude: a, blend: b } = propsRef.current;
      program.uniforms.uTime.value = ((now - start) / 1000) * s + 2.0;
      program.uniforms.uAmplitude.value = a;
      program.uniforms.uBlend.value = b;
      renderer.render({ scene: mesh });
      ctn.classList.add('aurora-ready');
      if (!reduceMotion) frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', resize);
      geometry.remove();
      program.remove();
      if (gl.canvas.parentNode === ctn) ctn.removeChild(gl.canvas);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [colorKey]);

  return <div ref={ctnDom} className="aurora-container" aria-hidden="true" />;
}
